import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import agentCapabilitiesService, { type AgentCapability } from '@/services/agent-capabilities.service';
import { SettingsPanel } from './settings/SettingsConsole';
import { useTranslation } from 'react-i18next';

const AgentCapabilitiesCard: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: capabilities = [], isLoading } = useQuery({
    queryKey: ['agent-capabilities'],
    queryFn: () => agentCapabilitiesService.list(),
  });

  const mutation = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      agentCapabilitiesService.set(key, enabled),
    onMutate: async ({ key, enabled }) => {
      await queryClient.cancelQueries({ queryKey: ['agent-capabilities'] });
      const prev = queryClient.getQueryData<AgentCapability[]>(['agent-capabilities']);
      queryClient.setQueryData<AgentCapability[]>(['agent-capabilities'], (old) =>
        (old || []).map((c) => (c.key === key ? { ...c, enabled } : c)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['agent-capabilities'], ctx.prev);
      toast.error(t('common:x1.capabilityChangeFailed', 'Fähigkeit konnte nicht geändert werden'));
    },
    onSuccess: (_d, v) => {
      toast.success(v.enabled ? 'Fähigkeit aktiviert' : 'Fähigkeit deaktiviert');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['agent-capabilities'] }),
  });

  return (
    <SettingsPanel
      title={t('common:x1.aiAgentCapabilities', 'KI-Agent Fähigkeiten')}
      description="Erlaube dem Cloud-Agent zusätzliche Aktionen. Wann/wie sie genutzt werden, steuerst du über die Trainingsdaten (Playbooks) unten."
      bodyClassName="space-y-3"
    >
      {isLoading ? (
        <div className="py-6 text-center text-sm text-slate-400">{t('common:x1.loadingDots', 'Wird geladen…')}</div>
      ) : (
        capabilities.filter((c) => !c.parent).map((c) => {
          const children = capabilities.filter((ch) => ch.parent === c.key);
          return (
            <div key={c.key} className="rounded-xl border border-slate-100 bg-white/60 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                    <ShieldCheck className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                      {c.label}
                      {c.enabled && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-600">
                          <Zap className="h-3 w-3" /> {t('common:state.active', 'Aktiv')}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs leading-relaxed text-slate-500">{c.description}</p>
                  </div>
                </div>
                <Switch
                  checked={c.enabled}
                  disabled={mutation.isPending}
                  onCheckedChange={(enabled) => mutation.mutate({ key: c.key, enabled })}
                />
              </div>

              {c.enabled && children.length > 0 && (
                <div className="mt-3 space-y-2 border-t border-slate-100 pl-12 pt-3">
                  {children.map((ch) => (
                    <div key={ch.key} className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700">{ch.label}</div>
                        <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{ch.description}</p>
                      </div>
                      <Switch
                        checked={ch.enabled}
                        disabled={mutation.isPending}
                        onCheckedChange={(enabled) => mutation.mutate({ key: ch.key, enabled })}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </SettingsPanel>
  );
};

export default AgentCapabilitiesCard;
